<div class="container-fluid escrow-audit-page" data-test="escrow-audit-page">
	<p-toast [style]="{marginTop: '80px'}"></p-toast>
	@if (formLoading) {
		<div class="progress-spinner">
			<app-spinner [formLoading]="formLoading"></app-spinner>
		</div>
	}

	<div class="page-header d-flex justify-content-between align-items-center">
		<div>
			<h2 data-test="escrow-audit-title">Escrow Calculation Audit</h2>
			<div class="page-subtitle">Per-reservation audit grid &mdash; CPA / outside counsel deliverable. Backed by HRS &sect; 467-14.</div>
		</div>
		<div class="d-flex gap-2 align-items-end">
			<div class="form-group as-of-picker">
				<label for="asOfDate">As of</label>
				<p-calendar inputId="asOfDate"
					[(ngModel)]="asOfDate"
					dateFormat="mm/dd/yy"
					[showIcon]="true"
					(onSelect)="onAsOfChange()"
					[appendTo]="'body'"
					data-test="escrow-audit-asof-picker">
				</p-calendar>
			</div>
			<button pButton type="button"
				label="Back to Report"
				icon="pi pi-arrow-left"
				class="p-button-text"
				(click)="gotoMain()"
				data-test="escrow-audit-back-btn">
			</button>
			<button pButton type="button"
				label="Refresh"
				icon="pi pi-refresh"
				class="p-button-text"
				(click)="loadReport()"
				data-test="escrow-audit-refresh-btn">
			</button>
			<button pButton type="button"
				label="Export to Excel"
				icon="pi pi-file-excel"
				class="p-button-success"
				[disabled]="formLoading || !filteredRows?.length"
				(click)="downloadExcel()"
				data-test="escrow-audit-export-excel-btn">
			</button>
			<button pButton type="button"
				label="Print to PDF"
				icon="pi pi-file-pdf"
				class="p-button-danger"
				[disabled]="formLoading || !filteredRows?.length"
				(click)="downloadPdf()"
				data-test="escrow-audit-export-pdf-btn">
			</button>
		</div>
	</div>

	<div class="filters-row d-flex justify-content-between align-items-center">
		<h3>All inputs &mdash; one row per reservation</h3>
		<div class="d-flex gap-2 filter-controls">
			<p-dropdown [options]="marketOptions"
				[(ngModel)]="marketFilter"
				(onChange)="applyFilters()"
				placeholder="Market"
				data-test="escrow-audit-market-filter">
			</p-dropdown>
			<span class="row-count">{{ filteredRows?.length || 0 }} / {{ rows?.length || 0 }} rows</span>
		</div>
	</div>

	<p-table #auditTable
		[value]="filteredRows"
		[paginator]="true"
		[rows]="50"
		[rowsPerPageOptions]="[25,50,100,250,500]"
		[showCurrentPageReport]="true"
		currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
		[globalFilterFields]="['confirmationNumber','guestName','propertyCode']"
		styleClass="p-datatable-sm escrow-audit-table"
		[scrollable]="true"
		scrollDirection="horizontal"
		data-test="escrow-audit-table">

		<ng-template pTemplate="caption">
			<div class="table-caption-row">
				<span class="p-input-icon-left">
					<i class="pi pi-search"></i>
					<input pInputText type="text" placeholder="Search..."
						(input)="auditTable.filterGlobal($any($event.target).value, 'contains')"
						data-test="escrow-audit-search">
				</span>
			</div>
		</ng-template>

		<ng-template pTemplate="header">
			<tr>
				<th pSortableColumn="confirmationNumber">Conf # <p-sortIcon field="confirmationNumber"></p-sortIcon></th>
				<th pSortableColumn="guestName">Guest <p-sortIcon field="guestName"></p-sortIcon></th>
				<th pSortableColumn="propertyCode">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
				<th pSortableColumn="market">Market <p-sortIcon field="market"></p-sortIcon></th>
				<th pSortableColumn="reservationStatus">Status (info) <p-sortIcon field="reservationStatus"></p-sortIcon></th>
				<th pSortableColumn="checkInDate">Check-in <p-sortIcon field="checkInDate"></p-sortIcon></th>
				<th pSortableColumn="checkOutDate">Check-out <p-sortIcon field="checkOutDate"></p-sortIcon></th>
				<th pSortableColumn="totalNights" class="text-right">Total Nights <p-sortIcon field="totalNights"></p-sortIcon></th>
				<th pSortableColumn="nightsConsumed" class="text-right">Consumed <p-sortIcon field="nightsConsumed"></p-sortIcon></th>
				<th pSortableColumn="nightsRemaining" class="text-right">Remaining <p-sortIcon field="nightsRemaining"></p-sortIcon></th>
				<th pSortableColumn="totalBilled" class="text-right">Total Billed <p-sortIcon field="totalBilled"></p-sortIcon></th>
				<th pSortableColumn="escrowEligibleBilled" class="text-right">Escrow-Eligible Billed <p-sortIcon field="escrowEligibleBilled"></p-sortIcon></th>
				<th pSortableColumn="nonEscrowBilled" class="text-right">Non-Escrow Billed <p-sortIcon field="nonEscrowBilled"></p-sortIcon></th>
				<!-- Change 2: renamed from "Net Cash Received" to "Payment Received" -->
				<th pSortableColumn="netCashHeld" class="text-right">Payment Received <p-sortIcon field="netCashHeld"></p-sortIcon></th>
				<th pSortableColumn="allocatedNetCash" class="text-right">Allocated Cash <p-sortIcon field="allocatedNetCash"></p-sortIcon></th>
				<th pSortableColumn="escrowRatio" class="text-right">Escrow Ratio <p-sortIcon field="escrowRatio"></p-sortIcon></th>
				<th pSortableColumn="escrowEligibleCash" class="text-right">Escrow-Eligible Cash <p-sortIcon field="escrowEligibleCash"></p-sortIcon></th>
				<th pSortableColumn="perNightEscrow" class="text-right">Per-Night Escrow <p-sortIcon field="perNightEscrow"></p-sortIcon></th>
				<th pSortableColumn="releasedToDate" class="text-right">Released to Date <p-sortIcon field="releasedToDate"></p-sortIcon></th>
				<th pSortableColumn="requiredInEscrow" class="text-right">Required Escrow <p-sortIcon field="requiredInEscrow"></p-sortIcon></th>
				<th pSortableColumn="exception">Exception <p-sortIcon field="exception"></p-sortIcon></th>
				<th pSortableColumn="bankTrailStatus">Bank Trail <p-sortIcon field="bankTrailStatus"></p-sortIcon></th>
				<th pSortableColumn="qboPostedAmount" class="text-right">QBO Posted <p-sortIcon field="qboPostedAmount"></p-sortIcon></th>
				<th pSortableColumn="discrepancy" class="text-right">Discrepancy <p-sortIcon field="discrepancy"></p-sortIcon></th>
			</tr>
		</ng-template>

		<ng-template pTemplate="body" let-row>
			<tr [attr.data-test-row]="row.reservationId" [class.exception-row]="row.exception">
				<td><strong>{{ row.confirmationNumber }}</strong></td>
				<td>{{ row.guestName }}</td>
				<td>{{ row.propertyCode }}</td>
				<td>{{ row.market === 'HAWAII' ? 'HI' : 'UT' }}</td>
				<td>{{ row.reservationStatus }}</td>
				<td>{{ formatDate(row.checkInDate) }}</td>
				<td>{{ formatDate(row.checkOutDate) }}</td>
				<td class="text-right num">{{ formatNumber(row.totalNights) }}</td>
				<td class="text-right num">{{ formatNumber(row.nightsConsumed) }}</td>
				<td class="text-right num">{{ formatNumber(row.nightsRemaining) }}</td>
				<td class="text-right num">{{ formatCurrency(row.totalBilled) }}</td>
				<td class="text-right num">{{ formatCurrency(row.escrowEligibleBilled) }}</td>
				<td class="text-right num">{{ formatCurrency(row.nonEscrowBilled) }}</td>
				<td class="text-right num">{{ formatCurrency(row.netCashHeld) }}</td>
				<td class="text-right num">{{ formatCurrency(row.allocatedNetCash) }}</td>
				<td class="text-right num">{{ formatRatio(row.escrowRatio) }}</td>
				<td class="text-right num">{{ formatCurrency(row.escrowEligibleCash) }}</td>
				<td class="text-right num">{{ formatCurrency(row.perNightEscrow) }}</td>
				<td class="text-right num">{{ formatCurrency(row.releasedToDate) }}</td>
				<td class="text-right num"><strong>{{ formatCurrency(row.requiredInEscrow) }}</strong></td>
				<td>
					@if (row.exception) {
						<span class="exception-pill">EXCEPTION</span>
					}
				</td>
				<td>{{ bankTrailLabel(row.bankTrailStatus) }}</td>
				<td class="text-right num">{{ row.qboPostedAmount != null ? formatCurrency(row.qboPostedAmount) : 'TBD' }}</td>
				<td class="text-right num">{{ row.discrepancy != null ? formatCurrency(row.discrepancy) : '' }}</td>
			</tr>
		</ng-template>

		<ng-template pTemplate="emptymessage">
			<tr>
				<td colspan="24" class="text-center empty-row">No reservations to audit on the selected date.</td>
			</tr>
		</ng-template>
	</p-table>

	<app-import-export
		importEnabled="false"
		[importDisabled]="true"
		[dataRetrived]="filteredRows"
		style="display:none">
	</app-import-export>
</div>
